import { BetType, ExoticBetTypePlacings } from '@/Constants/Bet.js';

/**
 * Basic factorial function
 *
 * @param n
 * @returns {number}
 */
const factorial = (n) => {
	if (n === 0) {
		return 1;
	}

	return n * factorial(n - 1);
};

/**
 * Return the possible combinations for Quinella
 *
 * @param selectionCount
 * @param positions
 * @returns {number}
 */
const quinellaFlexi = (selectionCount, positions = 2) => {
	let selectionCountDifference = selectionCount - positions;

	if (selectionCountDifference === 0) {
		return (selectionCount * (selectionCount - 1)) / 2;
	} else if (selectionCountDifference < 0) {
		return 0;
	} else {
		return factorial(selectionCount) / (factorial(positions) * factorial(selectionCountDifference));
	}
};

/**
 * Return the number of combinations for a Boxed Exotic
 *
 * @param selections
 * @param positions
 * @returns {number}
 */
const exoticFlexiBoxed = (selections = 0, positions = 2) => {
	if (selections < positions) {
		return 0;
	}

	return factorial(selections) / factorial(selections - positions);
};

/**
 * Return the number of combinations for a Boxed Exacta
 *
 * @param selections
 * @returns {number}
 */
const exactaFlexiBoxed = (selections) => {
	return exoticFlexiBoxed(selections, 2);
};

/**
 * Return the number of combinations for a Boxed Trifecta
 *
 * @param selections
 * @returns {number}
 */
const trifectaFlexiBoxed = (selections) => {
	return exoticFlexiBoxed(selections, 3);
};

/**
 * Return the number of combinations for a Boxed First Four
 *
 * @param selections
 * @returns {number}
 */
const firstfourFlexiBoxed = (selections) => {
	return exoticFlexiBoxed(selections, 4);
};

/**
 * Return the number of combinations for an Exacta
 *
 * @param selections
 * @returns {number}
 */
const exactaFlexi = (selections) => {
	const a = selections.filter((selection) => selection.position == 1);
	const b = selections.filter((selection) => selection.position == 2);

	let combinations = 0;
	for (let i of a) {
		for (let j of b) {
			if (i.id !== j.id) {
				combinations++;
			}
		}
	}

	return combinations;
};

/**
 * Return the number of combinations for a Trifecta
 *
 * @param selections
 * @returns {number}
 */
const trifectaFlexi = (selections) => {
	const a = selections.filter((selection) => selection.position == 1);
	const b = selections.filter((selection) => selection.position == 2);
	const c = selections.filter((selection) => selection.position == 3);

	let combinations = 0;
	for (let i of a) {
		for (let j of b) {
			for (let k of c) {
				if (i.id !== j.id && j.id !== k.id && k.id !== i.id) {
					combinations++;
				}
			}
		}
	}

	return combinations;
};

/**
 * Return the number of combinations for a First Four
 *
 * @param selections
 * @returns {number}
 */
const firstfourFlexi = (selections) => {
	const a = selections.filter((selection) => selection.position == 1);
	const b = selections.filter((selection) => selection.position == 2);
	const c = selections.filter((selection) => selection.position == 3);
	const d = selections.filter((selection) => selection.position == 4);

	let combinations = 0;
	for (let i of a) {
		for (let j of b) {
			for (let k of c) {
				for (let h of d) {
					if (i.id !== j.id && i.id !== k.id && i.id !== h.id && j.id !== k.id && j.id !== h.id && k.id !== h.id) {
						combinations++;
					}
				}
			}
		}
	}

	return combinations;
};

/**
 * Determine how we want to calculate the combination based on the exotic type
 *
 * @param exoticType
 * @param selections
 * @param isBoxed
 * @returns {number}
 */
export const calculateCombinations = (exoticType, selections = [], isBoxed = false) => {
	if (exoticType === BetType.Quinella) {
		return quinellaFlexi(selections.length);
	} else if (isBoxed) {
		switch (exoticType) {
			case BetType.Exacta:
				return exactaFlexiBoxed(selections.length);
			case BetType.Trifecta:
				return trifectaFlexiBoxed(selections.length);
			case BetType.FirstFour:
				return firstfourFlexiBoxed(selections.length);
			default:
				return 0;
		}
	} else {
		switch (exoticType) {
			case BetType.Exacta:
				return exactaFlexi(selections);
			case BetType.Trifecta:
				return trifectaFlexi(selections);
			case BetType.FirstFour:
				return firstfourFlexi(selections);
			case BetType.Quadrella:
				return firstfourFlexi(selections);
			default:
				return 0;
		}
	}
};

/**
 * Format Exotic Selections as needed per exotic type
 * selectionList is expected to be an array of objects
 * [{number:x, position,y}]
 * @param {*} selectionList
 * @param {*} betTypeId
 */
export const formatSelections = (selectionList, betTypeId) => {
	const groups = [];

	if (ExoticBetTypePlacings[betTypeId] !== undefined) {
		ExoticBetTypePlacings[betTypeId].forEach((position) => {
			let positionFound = false;
			const currentGroup = position;

			const groupEl = groups.find(({ group }) => currentGroup === group) ?? {
				group: currentGroup,
				selections: []
			};

			selectionList.forEach((selection) => {
				if (selection.position == position) {
					positionFound = true;

					if (groupEl.selections.length === 0) groups.push(groupEl);

					groupEl.selections.push(selection.number);
					groupEl.selections.sort((sel1, sel2) => sel1 - sel2);
				}
			});
			if (!positionFound) {
				if (groupEl.selections.length === 0) groups.push(groupEl);

				groupEl.selections.push('-');
			}
		});
		let isBoxed = false;
		selectionList.forEach((selection) => {
			// This indicates the selection list is a boxed bet, We can clear the groups and just show the boxed selections
			if (selection.position == null) {
				const groupEl = groups.find(({ group }) => 0 === group) ?? {
					group: 0,
					selections: []
				};

				if (groupEl.selections.length === 0) groups.push(groupEl);

				groupEl.selections.push(selection.number);
				groupEl.selections.sort((sel1, sel2) => sel1 - sel2);
				isBoxed = true;
			}
		});

		if (isBoxed) {
			// Remove all groups except group 0
			const boxedGroup = groups.find((g) => g.group === 0);
			groups.length = 0; // Clear the array
			if (boxedGroup) {
				groups.push(boxedGroup);
			}
		}
	} else {
		selectionList.forEach((selection) => {
			const currentGroup = betTypeId !== BetType.Quadrella ? selection.position : selection.race_number;

			const groupEl = groups.find(({ group }) => currentGroup === group) ?? {
				group: currentGroup,
				selections: []
			};

			if (groupEl.selections.length === 0) groups.push(groupEl);

			groupEl.selections.push(selection.number);
			groupEl.selections.sort((sel1, sel2) => sel1 - sel2);
		});
	}
	groups.sort((g1, g2) => g1.group - g2.group);
	return groups.map((group) => group.selections.join(',')).join('/');
};

/**
 * Is this bet type id an exotic format?
 * @param {*} betTypeId
 */
export const isExotic = (betTypeId) => {
	return (
		[
			BetType.Quinella,
			BetType.Exacta,
			BetType.Trifecta,
			BetType.FirstFour,
			BetType.Quadrella,
			BetType.EarlyQuadrella
		].indexOf(betTypeId) >= 0
	);
};
